import classnames from 'classnames';
import useNavButtons from 'components/Carousel/hooks/useNavButtons';
import useNavButtonsDisabled from 'components/Carousel/hooks/useNavButtonsDisabled';
import useOnSlideHandler from 'components/Carousel/hooks/useOnSlideHandler';
import useScrollTo from 'components/Carousel/hooks/useScrollTo';
import useViewport from 'hooks/useViewport';
import React, { useRef } from 'react';
import { Trek, useTrekker } from 'utils/analytics';

import SideButtons from './SideButtons';
import TopButtons from './TopButtons';

const CSS = require('./Carousel.module.scss');

interface CarouselProps {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  scrollTo?: number;
  onSlide?: (index: number) => void;
  onSideButton?: (direction: 'next' | 'previous') => void;
  isOrderedList?: boolean;
  isInverted?: boolean;
  previousButtonClassName?: string;
  nextButtonClassName?: string;
  noButtons?: boolean;
  topButtons?: boolean;
  gapOffset?: number;
}

function Carousel({
  children,
  className,
  scrollTo,
  onSlide,
  onSideButton,
  containerClassName,
  isOrderedList,
  isInverted,
  previousButtonClassName,
  nextButtonClassName,
  noButtons,
  topButtons,
  gapOffset,
}: CarouselProps) {
  const { width } = useViewport();
  const listRef = useRef<HTMLUListElement | HTMLOListElement>(null);
  const showNavButtons = useNavButtons(
    listRef,
    width,
    listRef.current?.children.length ?? 0,
  );
  const { prevButtonDisabled, nextButtonDisabled } =
    useNavButtonsDisabled(listRef);
  useScrollTo(listRef, scrollTo ?? -1);
  useOnSlideHandler(listRef, width, onSlide, gapOffset);
  const trekker = useTrekker();

  const onNext = () => {
    const list = listRef.current;
    if (!list || !list.children?.length) return;
    const element = list.children[0] as HTMLElement;
    const style =
      (element as any).currentStyle || window.getComputedStyle(element);
    const width = element.offsetWidth;
    const margin = parseFloat(style.marginLeft) + parseFloat(style.marginRight);

    if ((list as any).scrollBy) {
      (list as any).scrollBy({ left: width + margin, behavior: 'smooth' });
    } else {
      // IE11
      list.scrollLeft = list.scrollLeft + width + margin;
    }
    trekker.event('click', { component: ['carousel'], buttonText: 'next' });
    if (onSideButton && !nextButtonDisabled) {
      onSideButton('next');
    }
  };

  const onPrevious = () => {
    const list = listRef.current;
    if (!list || !list.children?.length) return;
    const element = list.children[0] as HTMLElement;
    const style =
      (element as any).currentStyle || window.getComputedStyle(element);
    const width = element.offsetWidth;
    const margin = parseFloat(style.marginLeft) + parseFloat(style.marginRight);
    trekker.event('click', { component: ['carousel'], buttonText: 'previous' });
    if ((list as any).scrollBy) {
      (list as any).scrollBy({ left: -width - margin, behavior: 'smooth' });
    } else {
      // IE 11
      list.scrollLeft = list.scrollLeft - width - margin;
    }

    if (onSideButton && !prevButtonDisabled) {
      onSideButton('previous');
    }
  };

  return (
    <Trek context={{ component: ['carousel'] }}>
      <div className={classnames(CSS.Container, containerClassName)}>
        {showNavButtons && topButtons && (
          <TopButtons
            prevButtonDisabled={prevButtonDisabled}
            nextButtonDisabled={nextButtonDisabled}
            onPrevious={onPrevious}
            onNext={onNext}
          />
        )}
        {isOrderedList ? (
          <ol
            className={classnames(CSS.List, className)}
            ref={listRef as React.RefObject<HTMLOListElement>}
          >
            {children}
          </ol>
        ) : (
          <ul
            className={classnames(CSS.List, className)}
            ref={listRef as React.RefObject<HTMLUListElement>}
          >
            {children}
          </ul>
        )}
        {showNavButtons && !noButtons && !topButtons && (
          <SideButtons
            prevButtonDisabled={prevButtonDisabled}
            nextButtonDisabled={nextButtonDisabled}
            previousButtonClassName={previousButtonClassName}
            nextButtonClassName={nextButtonClassName}
            onPrevious={onPrevious}
            onNext={onNext}
            isInverted={isInverted}
          />
        )}
      </div>
    </Trek>
  );
}

export default Carousel;
